import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import { Loader2Icon, WifiOffIcon } from 'lucide-react'
import { useEffect } from 'react'

import { BottomTabBar } from '@/components/bottom-tab-bar'
import { NavRail } from '@/components/nav-rail'
import { useAuth } from '@/hooks/use-auth'
import { useOnlineStatus } from '@/hooks/use-online-status'

export const Route = createFileRoute('/_app')({
  component: AppLayout,
})

const TAB_ROOTS = ['/conversas', '/contatos', '/ajustes']

function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, memberships, currentOrgId, loading, usingCachedSession } =
    useAuth()
  const pathname = location.pathname
  const returnTo =
    pathname === '/entrar' ? undefined : `${pathname}${location.searchStr}`

  useEffect(() => {
    if (loading) return
    if (!user) {
      void navigate({ to: '/entrar', search: { returnTo } })
    }
  }, [loading, user, navigate, returnTo])

  if (loading || !user) {
    return (
      <div className="bg-background flex min-h-dvh flex-1 items-center justify-center">
        <Loader2Icon className="text-muted-foreground size-6 animate-spin" />
      </div>
    )
  }

  if (memberships.length === 0 || !currentOrgId) {
    return (
      <div className="bg-background flex min-h-dvh flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="text-sm font-medium">Nenhuma organização</p>
        <p className="text-muted-foreground text-sm">
          Sua conta ainda não pertence a nenhuma organização. Acesse o painel
          web para configurar.
        </p>
      </div>
    )
  }

  // Bottom tabs only on the top-level tab routes; detail/chat views are full-screen.
  const showTabs = TAB_ROOTS.includes(pathname)

  return (
    <div className="bg-background flex min-h-0 flex-1 flex-col overflow-hidden">
      <OfflineBanner usingCachedSession={usingCachedSession} />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <NavRail />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </div>
      </div>
      {showTabs ? <BottomTabBar /> : null}
    </div>
  )
}

function OfflineBanner({
  usingCachedSession,
}: {
  usingCachedSession: boolean
}) {
  const online = useOnlineStatus()
  if (online && !usingCachedSession) return null
  return (
    <div className="flex shrink-0 items-center justify-center gap-2 bg-amber-500 px-3 py-1 text-xs font-medium text-amber-950">
      <WifiOffIcon className="size-3.5" />
      {online
        ? 'Conexão instável — usando sessão salva'
        : 'Sem conexão — exibindo dados salvos'}
    </div>
  )
}
